/**
 * Canonical voice-agent config module.
 *
 * Single source of truth for the `voice_agents.config` JSON shape, the
 * config sanitizer, and the per-agent tool enabled-state readers.
 *
 * Enabled-state contract:
 *   - `config.tools_enabled: Record<string, boolean>` is the current shape
 *     (same pattern as message agents).
 *   - `config.ai_tools.enabled_tools: string[]` is the legacy shape, read
 *     as a fallback during lazy migration: an explicit boolean in
 *     `tools_enabled` always wins; only tools absent from the map fall
 *     back to the legacy array.
 *   - ALL readers (tools GET/POST, ElevenLabs sync, tool-call webhook, PBX
 *     resync) must go through `isVoiceToolEnabled` / `getEnabledVoiceToolNames`
 *     — independent re-implementations are how split-brain rejections happen.
 */

import type { VoiceCalendarBookingConfig } from '@/lib/ai/tools/calendar/agent-calendar-config'
import type { CrmSchemaSnapshot } from '@/lib/ai/tools/crm-opportunity/snapshot'
import type { HandoverConfig } from '@/lib/ai/tools/human-handover/handover-config'
import type { DeliveryToolSettings } from '@/lib/deliveries/config'
import {
  DELIVERY_TOOL_NAMES,
  isDeliveryToolEnabled,
  isDeliveryToolName,
} from '@/lib/deliveries/config'

export type { VoiceCalendarBookingConfig }

/**
 * CRM opportunity tools storage — same shape as messaging/email agents.
 * `settings` holds the per-tool whitelists (written by the crm-tools
 * settings route); `schema_snapshot` is written ONLY by the Refresh-schema
 * endpoint and read by the sync-side resolver and the tool-call webhook.
 */
export interface VoiceCrmToolsConfig {
  settings?: {
    crm_update_opportunity_fields?: {
      allowed_field_codes: string[]
    }
    crm_move_opportunity_stage?: {
      allowed_stage_codes: string[]
    }
  }
  schema_snapshot?: CrmSchemaSnapshot
}

export interface VoiceAgentAiToolsConfig {
  /**
   * @deprecated Legacy enabled list — superseded by `tools_enabled` at the
   * config root. Never read directly; use `isVoiceToolEnabled`.
   */
  enabled_tools?: string[]
  /** Per-tool settings, keyed by tool name. */
  tool_settings?: Record<string, Record<string, unknown>>
}

export interface VoiceAgentKBSyncMapping {
  local_id: string
  elevenlabs_id: string
  synced_at: string
  name: string
}

export interface VoiceAgentRagSettings {
  chunk_limit?: number
  vector_distance_limit?: number
}

export interface VoiceAgentConfig {
  /** Applied agent role slugs (ai_use_case_roles); 'custom' is exclusive. */
  roles?: string[]
  /** Tenant-invented display name; only meaningful when roles === ['custom']. */
  role_label?: string | null
  /** Per-tool enabled state (current shape, mirrors message agents). */
  tools_enabled?: Record<string, boolean>
  /** Destinations and pause behavior for the human-escalation tool. */
  human_handover?: HandoverConfig
  /** Explicit per-agent Delivery master switch; absent defaults to disabled. */
  delivery_enabled?: boolean
  /** Delivery tool gates; synchronized to ElevenLabs only after voice safety validation. */
  delivery_tools?: DeliveryToolSettings
  ai_tools?: VoiceAgentAiToolsConfig
  /**
   * Calendar tools config (same CalendarBookingConfig shape as email
   * agents) + the EXPLICIT credential inbox: voice agents have no inbox
   * junction, so the operator picks which tenant email inbox's Google
   * OAuth token (with calendar scope) the calendar tools use.
   * Resolution lives in `lib/ai/tools/calendar/agent-calendar-config.ts`.
   */
  calendar_booking?: VoiceCalendarBookingConfig
  /** CRM opportunity tools: per-tool whitelists + stored schema snapshot. */
  crm_tools?: VoiceCrmToolsConfig
  /**
   * Per-agent Shopify shop domain for Storefront MCP tools
   * (search_shopify_catalog / search_shopify_policies). Same key as
   * messaging and email agent configs. When set, it overrides whatever
   * the LLM passes; when absent, tools fall back to the tenant's Shopify
   * integration.
   */
  shopify_shop_domain?: string | null
  knowledge_bases?: string[]
  knowledge_base_sync?: VoiceAgentKBSyncMapping[]
  rag_settings?: VoiceAgentRagSettings
  /** Other keys pass through untouched (config is an open JSON blob). */
  [key: string]: unknown
}

/**
 * Sanitize a raw `voice_agents.config` value.
 * Strips deprecated keys:
 *   - `call_transfer` — replaced by PBX flows
 *   - `kommo_integration` — dead blob; CRM config lives in `crm_bindings`
 * Stored blobs are stripped lazily: reads sanitize in memory, the next
 * config write persists the cleaned object.
 */
export function sanitizeVoiceAgentConfig(rawConfig: unknown): VoiceAgentConfig {
  if (!rawConfig || typeof rawConfig !== 'object') return {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { call_transfer, kommo_integration, ...rest } = rawConfig as Record<string, unknown>

  return rest as VoiceAgentConfig
}

/**
 * Is a tool enabled for this agent?
 * Explicit boolean in `tools_enabled` wins; absent keys fall back to the
 * legacy `ai_tools.enabled_tools` array; default is disabled.
 */
export function isVoiceToolEnabled(
  config: VoiceAgentConfig | null | undefined,
  toolName: string
): boolean {
  if (isDeliveryToolName(toolName)) {
    return isDeliveryToolEnabled(config?.delivery_tools, toolName)
  }
  const explicit = config?.tools_enabled?.[toolName]
  if (typeof explicit === 'boolean') return explicit
  return config?.ai_tools?.enabled_tools?.includes(toolName) ?? false
}

/**
 * All tool names currently enabled for this agent, merging the current map
 * with the legacy array under the same precedence as `isVoiceToolEnabled`.
 */
export function getEnabledVoiceToolNames(
  config: VoiceAgentConfig | null | undefined
): string[] {
  const enabled = new Set<string>()
  for (const [name, on] of Object.entries(config?.tools_enabled ?? {})) {
    if (on === true) enabled.add(name)
  }
  for (const name of config?.ai_tools?.enabled_tools ?? []) {
    // An explicit boolean in the map (true OR false) overrides the legacy
    // array — only unmapped names fall through.
    if (typeof config?.tools_enabled?.[name] === 'boolean') continue
    enabled.add(name)
  }
  if (config?.delivery_tools) {
    for (const name of DELIVERY_TOOL_NAMES) {
      if (isDeliveryToolEnabled(config.delivery_tools, name)) {
        enabled.add(name)
      } else {
        enabled.delete(name)
      }
    }
  }
  return [...enabled]
}

/**
 * Build the explicit `tools_enabled` map for a save: every available tool
 * gets a boolean, so later reads never fall back to the legacy array for
 * tools that were considered in this save.
 */
export function buildVoiceToolsEnabledMap(
  enabledNames: readonly string[],
  availableNames: readonly string[]
): Record<string, boolean> {
  const enabledSet = new Set(enabledNames)
  const map: Record<string, boolean> = {}
  for (const name of availableNames) {
    map[name] = enabledSet.has(name)
  }
  return map
}
